<template>
  <div class="activity-feed">
    <div class="skeleton-wrapper" v-if="!feedsToShow">
      <activity-skeleton :hide-team="true" />
    </div>
    <div class="empty-state thin" v-else-if="feedsToShow.length === 0">
      <template v-if="filterDetails && filterDetails.filterValue">
        We couldn't find a result matching the selected filter
      </template>
      <template v-else>
        Looks like there isn't any activity yet, but when things get going, this is where you’ll see
        the action.
      </template>
    </div>
    <template v-for="activity in feedsToShow" v-else>
      <outcome-activity
        class="event"
        :key="activityKey(activity)"
        :show-entity-info="true"
        :show-team-org-name="true"
        :activity="activity"
        :goal="activity.goal || (activity.initiative && activity.initiative.goal)"
        theme-color="secondary"
        @reacted="onReacted"
        v-if="isOutcomeActivity(activity)"
      />
      <checkin-activity
        class="event"
        :key="activityKey(activity)"
        :activity="activity"
        :kr="activity.keyResult"
        :show-entity-info="true"
        :show-team-org-name="true"
        :goal="activity.goal || (activity.initiative && activity.initiative.goal)"
        theme-color="secondary"
        @awarded="onAwarded"
        @reacted="onReacted"
        v-else-if="activity.type === GOAL_EVENT_TYPE.CHECKIN"
      />
      <comment-activity
        class="event"
        :key="activityKey(activity)"
        :activity="activity"
        :show-entity-info="true"
        :goal="(activity.initiative && activity.initiative.goal) || activity.goal"
        :project="activity.initiative && activity.initiative.project"
        theme-color="secondary"
        @awarded="onAwarded"
        @reacted="onReacted"
        v-else-if="activity.type === INITIATIVE_EVENT_TYPE.COMMENT"
      />
      <initiative-status-activity
        class="event"
        :key="activityKey(activity)"
        :activity="activity"
        :goal="activity.goal || (activity.initiative && activity.initiative.goal)"
        :project="activity.initiative.project"
        :show-entity-info="true"
        theme-color="secondary"
        @awarded="onAwarded"
        @reacted="onReacted"
        v-else-if="
          activity.type === INITIATIVE_EVENT_TYPE.FIELD_CHANGED && activity.field === statusFieldId
        "
      />
      <award-activity
        class="event"
        :key="activityKey(activity)"
        :activity="activity"
        :show-entity-info="true"
        :goal="(activity.initiative && activity.initiative.goal) || activity.goal"
        :project="activity.initiative && activity.initiative.project"
        theme-color="secondary"
        @reacted="onReacted"
        v-else-if="activity.type === INITIATIVE_EVENT_TYPE.AWARD"
      />
      <initiative-activity
        class="event"
        :key="activityKey(activity)"
        :activity="activity"
        :show-entity-info="true"
        :is-done-and-closed="activity.initiative.isDoneAndClosed"
        :goal="(activity.initiative && activity.initiative.goal) || activity.goal"
        :project="activity.initiative && activity.initiative.project"
        theme-color="secondary"
        @awarded="onAwarded"
        @reacted="onReacted"
        v-else-if="isInitiativeActivity(activity)"
      />
      <activity-view
        class="event"
        :key="activityKey(activity)"
        action-label="added a KR"
        :created-at="activity.goal.createdAt"
        :show-entity-info="true"
        :show-team-org-name="true"
        :activity="activity"
        :goal="activity.goal || (activity.initiative && activity.initiative.goal)"
        theme-color="secondary"
        @awarded="onAwarded"
        @reacted="onReacted"
        v-else-if="activity.type === GOAL_EVENT_TYPE.KR_CREATED"
      />
      <activity-view
        class="event"
        :key="activityKey(activity)"
        action-label="added a Goal"
        :created-at="activity.goal.createdAt"
        :show-entity-info="true"
        :show-team-org-name="true"
        :activity="activity"
        :goal="activity.goal || (activity.initiative && activity.initiative.goal)"
        theme-color="secondary"
        @awarded="onAwarded"
        @reacted="onReacted"
        v-else-if="isGoalCreationActivity(activity)"
      />
    </template>
    <link-button
      class="text-medium-1"
      @click="showMore = true"
      v-if="!showMore && feeds && feeds.length > 10"
      underline
      >Show more</link-button
    >
  </div>
</template>

<script>
import ActivitySkeleton from '@/components/activity/ActivitySkeleton'
import ActivityView from '@/components/activity/ActivityView'
import OutcomeActivity from '@/components/activity/OutcomeActivity'
import AwardActivity from '@/components/activity/AwardActivity'
import CheckinActivity from '@/components/activity/CheckinActivity'
import CommentActivity from '@/components/activity/CommentActivity'
import InitiativeStatusActivity from '@/components/activity/InitiativeStatusActivity'
import InitiativeActivity from '@/components/activity/InitiativeActivity'
import LinkButton from '@/components/buttons/LinkButton.vue'
import { EVENT_OTYPE } from '@/constants/awards'
import { GOAL_EVENT_TYPE } from '@/constants/goal'
import { INITIATIVE_EVENT_TYPE, INITIATIVE_FIELDS } from '@/constants/initiative'
import EventBusMixin from '@/mixins/EventBusMixin'
import MediaQueryMixin from '@/mixins/MediaQueryMixin'
import service from '@/service'
import cron from '@/services/cron'
import urlManager from '@/urlManager'
import { MILLIS_IN_A_MINUTE } from '@/utils/formatDate'
import showAPIError from '@/utils/showAPIError'

/**
 * This event shows the recent activity feeds of the organization
 */
export default {
  name: 'ActivityFeed',
  props: ['orgId', 'filterDetails', 'subOrgId'],
  data() {
    return {
      feeds: null,
      showMore: false,
      GOAL_EVENT_TYPE,
      INITIATIVE_EVENT_TYPE,
      statusFieldId: INITIATIVE_FIELDS.STATUS.id
    }
  },
  mixins: [EventBusMixin, MediaQueryMixin],
  components: {
    LinkButton,
    CheckinActivity,
    CommentActivity,
    InitiativeActivity,
    InitiativeStatusActivity,
    AwardActivity,
    ActivitySkeleton,
    OutcomeActivity,
    ActivityView
  },
  computed: {
    feedsToShow() {
      if (!this.feeds || this.showMore) return this.feeds
      return this.feeds.slice(0, 10)
    }
  },
  methods: {
    activityKey(activity) {
      return activity.otype + '_' + activity.id
    },
    async refreshFeeds(preventError) {
      try {
        let url = urlManager.allEvents(this.orgId)
        const params = {
          limit: this.isMobileMode ? 20 : 40,
          scope_id: this.subOrgId
        }
        if (this.filterDetails?.filterValue) {
          params[this.filterDetails.filterName] = this.filterDetails.filterValue
        }
        const response = await service.client.get(url, {
          params
        })
        this.feeds = response.data
      } catch (e) {
        console.log('Failed to fetch activity feed because', e)
        !preventError && showAPIError(e, undefined, 'Failed to fetch activity feed')
      }
    },
    onAwarded() {
      this.refreshFeeds()
    },
    onReacted() {
      this.refreshFeeds()
    },
    isInitiativeActivity(activity) {
      const initiativeEvents = [
        INITIATIVE_EVENT_TYPE.CREATED,
        INITIATIVE_EVENT_TYPE.REOPENED,
        INITIATIVE_EVENT_TYPE.CLOSED
      ]
      return (
        activity.otype === EVENT_OTYPE.INITIATIVE_EVENT && initiativeEvents.includes(activity.type)
      )
    },
    isGoalCreationActivity(activity) {
      return activity.type === GOAL_EVENT_TYPE.CREATED && activity.otype === EVENT_OTYPE.GOAL_EVENT
    },
    isOutcomeActivity(activity) {
      return [GOAL_EVENT_TYPE.CLOSED, GOAL_EVENT_TYPE.REOPENED].includes(activity.type)
    }
  },
  async mounted() {
    this.refreshFeeds()
    // poll for feeds
    this.cron = cron.start(this.refreshFeeds.bind(this, true), {
      interval: MILLIS_IN_A_MINUTE,
      tabBlurInterval: MILLIS_IN_A_MINUTE * 60,
      blurRefetchTimeout: 10000
    })
  },
  beforeDestroy() {
    this.cron.stop()
  },
  watch: {
    filterDetails() {
      this.refreshFeeds()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.event:not(:last-child) {
  margin-bottom: 8px;
}

.skeleton-wrapper {
  padding-top: 10px;
}

.empty-state {
  max-width: 330px;
  text-align: center;
  margin: 25px auto;
}

.event {
  padding: 12px;
  border-radius: 5px;
  background-color: $selectionColor;
}
</style>
